/*global Rollbar */
import { useState, useEffect } from 'react';

// ============================================================================
// Clock Drift Detection with NTP-style Calibration
// ============================================================================
//
// Problem: When judges submit scores, the createdAt timestamp comes from their
// local clock. If their clock is ahead of the server, scores appear to be
// "from the future" and get filtered out during score calculation.
//
// Solution: Use NTP-style measurement to accurately detect clock drift by
// accounting for network round-trip time (RTT).
//
// NTP Formula: drift = ((t1 + t4) / 2) - serverTime
//   t1 = client time when request was sent
//   t4 = client time when response was received
//   serverTime = time from server's Date header
//   (t1 + t4) / 2 = estimated client time when server generated response
//
// Dangerous drift directions:
//   - JUDGE with clock AHEAD: their scores appear in the future
//   - EVALUATOR with clock BEHIND: all scores appear in the future
// ============================================================================

// State
let currentDrift = null;
let calibratedDrift = null;
let lastServerTime = null;
let lastClientTime = null;
let lastRtt = null;
let calibrationSamples = [];
let isCalibrated = false;
let debugMode = false;

const listeners = new Set();

// Thresholds
const DRIFT_WARNING_THRESHOLD_MS = 2000; // 2 seconds for warning display
const DRIFT_SCORING_THRESHOLD_MS = 1000; // 1 second matches nowBuffer in scoring.js
const CALIBRATION_SAMPLE_COUNT = 5;

// Check for debug mode from URL
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  debugMode = params.get('clockDebug') === 'true';
}

function notify() {
  listeners.forEach((listener) => listener(currentDrift));
}

function debugLog(...args) {
  if (debugMode) {
    console.log('[ClockDrift]', ...args);
  }
}

/**
 * Update the clock drift based on a server timestamp with RTT awareness.
 * Uses NTP-style calculation: drift = ((t1 + t4) / 2) - serverTime
 *
 * @param {string} serverDateHeader - The Date header from the server response
 * @param {number} t1 - Client timestamp when request was sent (ms)
 * @param {number} t4 - Client timestamp when response was received (ms)
 */
export function updateClockDrift(serverDateHeader, t1 = null, t4 = null) {
  if (!serverDateHeader) return;

  const serverTime = new Date(serverDateHeader).getTime();
  if (isNaN(serverTime)) return;

  // If t1/t4 not provided, fall back to legacy behavior (less accurate)
  const clientTime = t4 || Date.now();
  const rtt = t1 && t4 ? t4 - t1 : null;

  lastServerTime = serverTime;
  lastClientTime = clientTime;
  lastRtt = rtt;

  // Calculate drift using NTP-style formula if we have RTT
  // Otherwise fall back to simple difference
  let drift;
  if (rtt !== null) {
    // NTP formula: client midpoint time - server time
    const clientMidpoint = (t1 + t4) / 2;
    drift = clientMidpoint - serverTime;
    debugLog('NTP-style drift calculation:', {
      t1: new Date(t1).toISOString(),
      t4: new Date(t4).toISOString(),
      serverTime: serverDateHeader,
      rtt,
      clientMidpoint: new Date(clientMidpoint).toISOString(),
      drift,
    });
  } else {
    // Legacy: simple difference (includes unknown network latency)
    drift = clientTime - serverTime;
    debugLog('Legacy drift calculation (no RTT):', {
      clientTime,
      serverTime,
      drift,
    });
  }

  currentDrift = drift;

  // Store calibration sample if we have RTT data
  if (rtt !== null && !isCalibrated) {
    calibrationSamples.push({ drift, rtt, timestamp: clientTime });
    debugLog(
      `Calibration sample ${calibrationSamples.length}/${CALIBRATION_SAMPLE_COUNT}:`,
      {
        drift,
        rtt,
      }
    );

    // Once we have enough samples, calibrate using the best one
    if (calibrationSamples.length >= CALIBRATION_SAMPLE_COUNT) {
      calibrate();
    }
  }

  // Log significant drift
  if (Math.abs(drift) > DRIFT_WARNING_THRESHOLD_MS) {
    console.warn(`Clock drift detected: ${(drift / 1000).toFixed(1)}s`, {
      clientTime: new Date(clientTime).toISOString(),
      serverTime: serverDateHeader,
      rtt,
      isCalibrated,
    });
  }

  notify();
}

// Calibration quality tracking
let calibrationQuality = 'unknown'; // 'good', 'fair', 'poor', 'unknown'
let calibrationVariance = null;

/**
 * Perform calibration using collected samples.
 * Uses robust statistics: removes outliers, then takes median.
 */
function calibrate() {
  if (calibrationSamples.length === 0) return;

  const drifts = calibrationSamples.map((s) => s.drift);
  const rtts = calibrationSamples.map((s) => s.rtt);

  // Step 1: Determine majority direction (positive = ahead, negative = behind)
  const positiveCount = drifts.filter((d) => d > 0).length;
  const negativeCount = drifts.filter((d) => d < 0).length;
  const majorityDirection = positiveCount > negativeCount ? 'ahead' : 'behind';

  // Step 2: Remove outliers (samples with opposite direction from majority)
  const filteredSamples = calibrationSamples.filter((s) => {
    if (majorityDirection === 'ahead') return s.drift >= 0;
    return s.drift <= 0;
  });

  // If we filtered too many, fall back to all samples
  const samplesToUse =
    filteredSamples.length >= 2 ? filteredSamples : calibrationSamples;
  const outliersRemoved = calibrationSamples.length - samplesToUse.length;

  // Step 3: Calculate median drift (more robust than mean or min-RTT)
  const sortedDrifts = samplesToUse.map((s) => s.drift).sort((a, b) => a - b);
  const medianIndex = Math.floor(sortedDrifts.length / 2);
  const medianDrift =
    sortedDrifts.length % 2 === 0
      ? (sortedDrifts[medianIndex - 1] + sortedDrifts[medianIndex]) / 2
      : sortedDrifts[medianIndex];

  // Step 4: Calculate variance to assess reliability
  const mean =
    samplesToUse.reduce((sum, s) => sum + s.drift, 0) / samplesToUse.length;
  const variance =
    samplesToUse.reduce((sum, s) => sum + Math.pow(s.drift - mean, 2), 0) /
    samplesToUse.length;
  const stdDev = Math.sqrt(variance);
  calibrationVariance = stdDev;

  // Step 5: Determine calibration quality
  const minRtt = Math.min(...rtts);
  const maxRtt = Math.max(...rtts);

  if (stdDev < 500 && minRtt < 1000) {
    calibrationQuality = 'good';
  } else if (stdDev < 1000 && minRtt < 2000) {
    calibrationQuality = 'fair';
  } else {
    calibrationQuality = 'poor';
  }

  calibratedDrift = medianDrift;
  isCalibrated = true;

  debugLog('Calibration complete:', {
    samples: calibrationSamples.length,
    outliersRemoved,
    majorityDirection,
    medianDrift,
    stdDev,
    quality: calibrationQuality,
    allSamples: calibrationSamples.map((s) => ({ drift: s.drift, rtt: s.rtt })),
    usedSamples: samplesToUse.map((s) => ({ drift: s.drift, rtt: s.rtt })),
  });

  const qualityEmoji =
    calibrationQuality === 'good'
      ? '✓'
      : calibrationQuality === 'fair'
      ? '~'
      : '⚠';
  console.log(
    `[ClockDrift] Calibrated: ${(calibratedDrift / 1000).toFixed(2)}s drift ` +
      `(${qualityEmoji} ${calibrationQuality}, stdDev: ${stdDev.toFixed(
        0
      )}ms, ` +
      `RTT: ${minRtt}-${maxRtt}ms, outliers removed: ${outliersRemoved})`
  );
}

/**
 * Manually trigger recalibration.
 * Clears existing samples and starts fresh.
 */
export function recalibrate() {
  calibrationSamples = [];
  isCalibrated = false;
  calibratedDrift = null;
  calibrationQuality = 'unknown';
  calibrationVariance = null;
  debugLog('Recalibration triggered - collecting new samples');
}

/**
 * Get the current clock drift in milliseconds.
 * Returns calibrated drift if available, otherwise current measurement.
 * Positive = client ahead, Negative = client behind.
 */
export function getClockDrift() {
  return isCalibrated ? calibratedDrift : currentDrift;
}

/**
 * Get raw (uncalibrated) drift for comparison.
 */
export function getRawDrift() {
  return currentDrift;
}

/**
 * Get formatted drift info for display.
 * @param {string} role - Optional role for context-aware warnings ('JUDGE', 'ADMIN', etc.)
 */
export function getClockDriftInfo(role = null) {
  const drift = getClockDrift();

  if (drift === null) {
    return {
      drift: null,
      status: 'unknown',
      message: 'Checking...',
      isCalibrated: false,
    };
  }

  const absDrift = Math.abs(drift);
  const seconds = absDrift / 1000;
  const direction = drift > 0 ? 'ahead' : 'behind';

  // Determine if this drift direction is problematic for the role
  // JUDGE with clock AHEAD = bad (scores appear in future)
  // ADMIN/PRODUCER with clock BEHIND = bad (scores appear in future when they finalize)
  const isProblematicDirection =
    (role === 'JUDGE' && drift > 0) ||
    (['ADMIN', 'PRODUCER'].includes(role) && drift < 0) ||
    role === null; // If no role, warn for both directions

  let status = 'ok';
  let message = 'Clock synced';

  // Determine status and message based on drift severity
  // Thresholds: scoring (1s) < warning display (2s) < error (5s)
  if (isProblematicDirection) {
    if (seconds > 5) {
      status = 'error';
      message = `${seconds.toFixed(1)}s ${direction}`;
    } else if (seconds > DRIFT_WARNING_THRESHOLD_MS / 1000) {
      status = 'warning';
      message = `${seconds.toFixed(1)}s ${direction}`;
    } else if (seconds > DRIFT_SCORING_THRESHOLD_MS / 1000) {
      // Between scoring threshold (1s) and warning threshold (2s)
      // Set message but keep status as 'ok' (hasScoringRisk handles the alert)
      status = 'caution';
      message = `${seconds.toFixed(1)}s ${direction}`;
    }
  }

  return {
    drift,
    rawDrift: currentDrift,
    calibratedDrift,
    absDrift,
    direction,
    status,
    message,
    isCalibrated,
    calibrationQuality,
    calibrationVariance,
    lastServerTime,
    lastClientTime,
    lastRtt,
    isProblematicDirection,
    role,
  };
}

/**
 * Check if drift would cause scoring issues (uses tighter threshold).
 * @param {string} role - Role to check ('JUDGE', 'ADMIN', etc.)
 */
export function hasScoringRisk(role) {
  const drift = getClockDrift();
  if (drift === null) return false;

  const absDrift = Math.abs(drift);

  // Check direction-specific risk
  if (role === 'JUDGE' && drift > DRIFT_SCORING_THRESHOLD_MS) {
    return true; // Judge clock ahead by more than buffer
  }
  if (
    ['ADMIN', 'PRODUCER'].includes(role) &&
    drift < -DRIFT_SCORING_THRESHOLD_MS
  ) {
    return true; // Admin/Producer clock behind by more than buffer
  }

  // Generic check for unknown roles
  if (!role && absDrift > DRIFT_SCORING_THRESHOLD_MS) {
    return true;
  }

  return false;
}

/**
 * Hook to subscribe to clock drift updates.
 * Returns drift info object with status and message.
 * @param {string} role - Optional role for context-aware status
 */
export function useClockDrift(role = null) {
  const [driftInfo, setDriftInfo] = useState(() => getClockDriftInfo(role));

  useEffect(() => {
    const listener = () => {
      setDriftInfo(getClockDriftInfo(role));
    };

    listeners.add(listener);

    // Get initial value if available
    if (currentDrift !== null) {
      listener();
    }

    return () => {
      listeners.delete(listener);
    };
  }, [role]);

  return driftInfo;
}

/**
 * Report drift to Rollbar if it exceeds threshold.
 * Call this at critical moments (e.g., before finalizing scores).
 */
export function reportDriftIfSignificant(context = {}) {
  const drift = getClockDrift();

  if (drift !== null && Math.abs(drift) > DRIFT_SCORING_THRESHOLD_MS) {
    Rollbar.warning('Clock drift detected during operation', {
      driftMs: drift,
      driftSeconds: (drift / 1000).toFixed(1),
      isCalibrated,
      calibratedDrift,
      rawDrift: currentDrift,
      lastRtt,
      lastServerTime: lastServerTime
        ? new Date(lastServerTime).toISOString()
        : null,
      lastClientTime: lastClientTime
        ? new Date(lastClientTime).toISOString()
        : null,
      ...context,
    });
    return true;
  }
  return false;
}

/**
 * Get full diagnostics for debugging.
 * Access via ?clockDebug=true URL parameter.
 */
export function getDiagnostics() {
  return {
    currentDrift,
    calibratedDrift,
    isCalibrated,
    calibrationQuality,
    calibrationVariance,
    lastServerTime: lastServerTime
      ? new Date(lastServerTime).toISOString()
      : null,
    lastClientTime: lastClientTime
      ? new Date(lastClientTime).toISOString()
      : null,
    lastRtt,
    calibrationSamples: calibrationSamples.map((s) => ({
      drift: s.drift,
      rtt: s.rtt,
      timestamp: new Date(s.timestamp).toISOString(),
    })),
    thresholds: {
      warning: DRIFT_WARNING_THRESHOLD_MS,
      scoring: DRIFT_SCORING_THRESHOLD_MS,
      calibrationSamples: CALIBRATION_SAMPLE_COUNT,
    },
    debugMode,
  };
}

/**
 * Enable or disable debug mode programmatically.
 */
export function setDebugMode(enabled) {
  debugMode = enabled;
  if (enabled) {
    console.log(
      '[ClockDrift] Debug mode enabled. Current diagnostics:',
      getDiagnostics()
    );
  }
}

// Expose diagnostics globally for console debugging
if (typeof window !== 'undefined') {
  window.__clockDriftDiagnostics = getDiagnostics;
  window.__clockDriftRecalibrate = recalibrate;
  window.__clockDriftSetDebug = setDebugMode;
}
